export type OfficePlatform =
  | "PC"
  | "Mac"
  | "OfficeOnline"
  | "iOS"
  | "Android"
  | "Universal"
  | "Unknown";

const KNOWN_PLATFORMS: readonly OfficePlatform[] = [
  "PC",
  "Mac",
  "OfficeOnline",
  "iOS",
  "Android",
  "Universal",
];

export const OUTLOOK_WEB_HOST_NAME = "OutlookWebApp";

export type AuthEnvironment = {
  platform: OfficePlatform;
  hostName?: string;
  hostVersion?: string;
  officeVersion?: string;
  userAgent: string;
  isWebView2: boolean;
  isOutlookWeb: boolean;
  naaSupported: boolean;
  identityApiSupported: boolean;
};

type OfficeLike = {
  context?: {
    platform?: unknown;
    diagnostics?: { version?: string };
    requirements?: { isSetSupported?: (name: string, version: string) => boolean };
    mailbox?: { diagnostics?: { hostName?: string; hostVersion?: string } };
  };
};

function readPlatform(office: OfficeLike): OfficePlatform {
  const raw = office.context?.platform;
  const value = typeof raw === "string" ? raw : (raw as { toString?: () => string } | undefined)?.toString?.();
  return KNOWN_PLATFORMS.includes(value as OfficePlatform) ? (value as OfficePlatform) : "Unknown";
}

function isSupported(office: OfficeLike, name: string, version: string): boolean {
  try {
    const fn = office.context?.requirements?.isSetSupported;
    if (typeof fn === "function") {
      return fn.call(office.context?.requirements, name, version) === true;
    }
  } catch {
    return false;
  }
  return false;
}

let cached: AuthEnvironment | null = null;

export function detectAuthEnvironment(force = false): AuthEnvironment {
  if (cached && !force) return cached;

  const office: OfficeLike = typeof Office !== "undefined" ? (Office as unknown as OfficeLike) : {};
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const platform = readPlatform(office);
  const hostName = office.context?.mailbox?.diagnostics?.hostName;
  const hostVersion = office.context?.mailbox?.diagnostics?.hostVersion;
  const officeVersion = office.context?.diagnostics?.version;

  const isWebView2 = /Edg\//i.test(userAgent) && /Windows NT/i.test(userAgent);
  const isOutlookWeb = hostName === OUTLOOK_WEB_HOST_NAME || platform === "OfficeOnline";

  const naaSupported = isSupported(office, "NestedAppAuth", "1.1");
  const identityApiSupported =
    isSupported(office, "IdentityAPI", "1.3") || isSupported(office, "IdentityAPI", "1.0");

  cached = {
    platform,
    hostName,
    hostVersion,
    officeVersion,
    userAgent,
    isWebView2,
    isOutlookWeb,
    naaSupported,
    identityApiSupported,
  };
  return cached;
}

export function resetAuthEnvironmentCache(): void {
  cached = null;
}
